import { useLoading } from '@/providers/LoadingProvider';
import { useCurrentTeamMember } from '@/providers/TeamMemberProvider';
import { useCurrentPurchaseOrder } from '@/providers/PurchaseOrderProvider';
import {
  usePurchaseOrderAccountings,
  usePurchaseOrderAccountingsInvalidate,
} from '@/hooks/api/0720_purchase_order_accountings';
import { PurchaseOrderAccountingLogic, PurchaseOrderLogic } from '@/hooks/api/models/logics';
import { PurchaseOrderAccounting } from '@/hooks/api/models';
import { Dispatch, SetStateAction, useEffect, useState } from 'react';
import { ModalConfirm } from '@/components/element/modal/ModalConfirm';
import { useDestroyPurchaseOrderAccounting } from '@/hooks/api/0750_destroy_purchase_order_accounting';
import { Link } from 'react-router-dom';
import { useUpdatePurchaseOrderAccounting } from '@/hooks/api/0740_update_purchase_order_accounting';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import { toastError } from '@/lib/toast';
import { usePurchaseOrderInvalidate } from '@/hooks/api/0570_purchase_order';
import { InputPrice } from '@/components/element/Form/InputPrice';

export const PurchaseOrderAccountingsElement = () => {
  const { setShowLoading } = useLoading();
  const { _w_, _t_, me, meTeamMember } = useCurrentTeamMember();
  const { _po_, mePurchaseOrderMember } = useCurrentPurchaseOrder();
  const { data: accountings } = usePurchaseOrderAccountings({
    identifier: _w_!.identifier,
    team_id: _t_!.id,
    id: _po_!.id,
  });
  const [delTarget, setDelTarget] = useState<PurchaseOrderAccounting>();
  const {
    mutate: destroyMutate,
    isSuccess: destroyIsSuccess,
    isPending: destroyIsPending,
  } = useDestroyPurchaseOrderAccounting();
  const { invalidate } = usePurchaseOrderAccountingsInvalidate({
    identifier: _w_!.identifier,
    team_id: _t_!.id,
    id: _po_!.id,
  });
  const { invalidate: invalidatePurchaseOrder } = usePurchaseOrderInvalidate({
    identifier: _w_!.identifier,
    team_id: _t_!.id,
    id: _po_!.id,
  });

  useEffect(() => {
    if (destroyIsSuccess) {
      invalidate();
      invalidatePurchaseOrder();
    }
  }, [destroyIsSuccess]);

  useEffect(() => setShowLoading(destroyIsPending), [destroyIsPending]);

  const onDeleteClick = () => {
    setShowLoading(true);
    destroyMutate({
      identifier: _w_!.identifier,
      team_id: _t_!.id,
      purchase_order_id: _po_!.id,
      purchase_order_accounting_id: delTarget!.id,
    });
  };

  return (
    <div className="table-responsive">
      <ModalConfirm
        modalId={`delPurchaseOrderAccountingConfirm`}
        okClicked={() => onDeleteClick()}
        message={'削除します。よろしいですか？'}
      />

      <table className="table align-items-center">
        <thead>
          <tr>
            <th
              className="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7 px-2 text-end"
              style={{ width: 80 }}
            >
              計上月
            </th>
            <th className="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7 px-2 text-end">
              一括支払金額（税抜）
            </th>
            <th className="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7 px-2 text-end">
              月額支払金額（税抜）
            </th>
            <th className="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7 px-2 text-end">合計</th>
            <th className="text-secondary opacity-7 text-end" style={{ width: 110 }}></th>
          </tr>
        </thead>
        <tbody>
          {accountings?.map((d) => (
            <PurchaseOrderAccountingRow key={`poa-${d.id}`} d={d} setDelTarget={setDelTarget} />
          ))}
          <tr>
            <td className="text-xs text-dark font-weight-bold text-end">合計金額</td>
            <td className="text-xs text-dark font-weight-bold text-end">
              ¥{PurchaseOrderAccountingLogic.calcTotalSpotAmount(accountings || []).toLocaleString()}
            </td>
            <td className="text-xs text-dark font-weight-bold text-end">
              ¥{PurchaseOrderAccountingLogic.calcTotalMonthlyAmount(accountings || []).toLocaleString()}
            </td>
            <td className="text-xs text-dark font-weight-bold text-end">
              ¥{PurchaseOrderAccountingLogic.calcTotalAmount(accountings || []).toLocaleString()}
            </td>
            <td></td>
          </tr>
        </tbody>
      </table>
    </div>
  );
};

type FormInputs = {
  identifier: string;
  team_id: number;
  purchase_order_id: number;
  purchase_order_accounting_id: number;
  monthly_amount?: number;
  spot_amount?: number;
};

const PurchaseOrderAccountingRow = ({
  d,
  setDelTarget,
}: {
  d: PurchaseOrderAccounting;
  setDelTarget: Dispatch<SetStateAction<PurchaseOrderAccounting | undefined>>;
}) => {
  const { setShowLoading } = useLoading();
  const { _w_, _t_, me, meTeamMember } = useCurrentTeamMember();
  const { _po_, mePurchaseOrderMember } = useCurrentPurchaseOrder();
  const [editing, setEditing] = useState<boolean>(false);
  const [editable, setEditable] = useState<boolean>(false);
  const { mutate, data, error, isPending } = useUpdatePurchaseOrderAccounting();
  const { invalidate } = usePurchaseOrderAccountingsInvalidate({
    identifier: _w_!.identifier,
    team_id: _t_!.id,
    id: _po_!.id,
  });
  const { invalidate: invalidatePurchaseOrder } = usePurchaseOrderInvalidate({
    identifier: _w_!.identifier,
    team_id: _t_!.id,
    id: _po_!.id,
  });

  const {
    control,
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<FormInputs>({
    mode: 'onBlur',
    criteriaMode: 'all',
    defaultValues: {
      identifier: _w_!.identifier,
      team_id: _t_!.id,
      purchase_order_id: _po_!.id,
      purchase_order_accounting_id: d.id,
      spot_amount: d.spot_detail?.amount || 0,
      monthly_amount: d.monthly_detail?.amount || 0,
    },
  });

  useEffect(() => setShowLoading(isPending), [isPending]);

  const onSubmit: SubmitHandler<FormInputs> = (inputData) => {
    setShowLoading(true);
    mutate(inputData);
  };

  useEffect(() => {
    if (data) {
      invalidate();
      invalidatePurchaseOrder();
      setEditing(false);
    }
  }, [data]);

  useEffect(() => {
    if (error) {
      const msgs: string[] = (error as any).response.data.errors || [];
      if (msgs.length > 0) toastError(msgs[0]);
    }
  }, [error]);

  useEffect(() => {
    setEditable(_po_!.accounting_manual_mode && PurchaseOrderLogic.canUpdateAccounting(_t_!, _po_!, me!));
  }, [_po_!.accounting_manual_mode]);

  if (!editing) {
    return (
      <tr key={`poa-${d.id}`}>
        <td className="text-xs text-dark text-end">{d.year_month}</td>
        <td className="text-xs text-dark text-end">¥{PurchaseOrderAccountingLogic.spotAmount(d).toLocaleString()}</td>
        <td className="text-xs text-dark text-end">
          ¥{PurchaseOrderAccountingLogic.monthlyAmount(d).toLocaleString()}
        </td>
        <td className="text-xs text-dark text-end">¥{PurchaseOrderAccountingLogic.totalAmount(d).toLocaleString()}</td>
        <td className="text-end">
          <Link
            to=""
            className={`btn btn-icon-only bg-gradient-success me-1 ${!editable && 'disabled'}`}
            onClick={() => setEditing((e) => !e)}
          >
            <i className="fas fa-edit"></i>
          </Link>
          <Link
            to=""
            className={`btn btn-icon-only bg-gradient-danger ${!editable && 'disabled'}`}
            data-bs-toggle="modal"
            data-bs-target={`#delPurchaseOrderAccountingConfirm`}
            onClick={() => setDelTarget(d)}
          >
            <i className="fas fa-trash-alt"></i>
          </Link>
        </td>
      </tr>
    );
  }

  return (
    <tr key={`poa-${d.id}`}>
      <td className="text-xs text-dark text-end">{d.year_month}</td>
      <td className="text-xs text-dark">
        <div className="input-group me-2">
          <Controller
            render={({ field: { onChange, value, ...field } }) => <InputPrice onChange={onChange} value={value} />}
            control={control}
            name="spot_amount"
          />
        </div>
      </td>
      <td className="text-xs text-dark">
        <div className="input-group me-2">
          <Controller
            render={({ field: { onChange, value, ...field } }) => <InputPrice onChange={onChange} value={value} />}
            control={control}
            name="monthly_amount"
          />
        </div>
      </td>
      <td className="text-xs text-dark text-end">¥{PurchaseOrderAccountingLogic.totalAmount(d).toLocaleString()}</td>
      <td className="text-end">
        <button className="btn btn-icon-only btn-outline-secondary me-1" onClick={() => setEditing((e) => !e)}>
          <i className="fas fa-undo"></i>
        </button>
        <button className="btn btn-icon-only bg-gradient-dark" onClick={() => handleSubmit(onSubmit)()}>
          <i className="fas fa-check-circle"></i>
        </button>
      </td>
    </tr>
  );
};
